import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import React from 'react';

import { Typography } from '@mui/material';
import styles from 'features/DefaultHelp.module.css';

const HelpDialog = () => {
    const instructions = [
        {
            key: 'search',
            title: 'Inventory Check Search',
        },
        {
            key: 'filter',
            title: 'Inventory Check Statuses Filter',
        },
        {
            key: 'addNew',
            title: 'Starting a new Inventory Check',
        },
        {
            key: 'open',
            title: 'Open Inventory Check',
        },
        {
            key: 'remove',
            title: 'Remove Inventory Check',
        },
    ];

    const getInstructionComponent = (key) => {
        switch (key) {
            case 'filter':
                return renderFilterInstruction();
            case 'addNew':
                return renderAddNewInstruction();
            case 'open':
                return renderOpenInstruction();
            case 'remove':
                return renderRemoveInstruction();
            default:
                return renderSearchInstruction();
        }
    };

    const renderFilterInstruction = () => (
        <Box className={styles['instruction-child-container']}>
            <List className={styles['ordered-list']}>
                <ListItemText className={styles['list-item']}>
                    <span>By default, all statuses are selected, and all Inventory Checks are displayed in the list.</span>
                </ListItemText>
                <ListItemText className={styles['list-item']}>
                    <span>You can turn on/off any status filter.</span>
                </ListItemText>
                <ListItemText className={styles['list-item']}>
                    <span>The Inventory Check List will display Inventory Checks according to the turned-on filters.</span>
                </ListItemText>
                <ListItemText className={styles['list-item']}>
                    <span>A message will appear if no Inventory Check matches the selected filters.</span>
                </ListItemText>
            </List>
        </Box>
    );

    const renderAddNewInstruction = () => (
        <Box className={styles['instruction-child-container']}>
            <List className={styles['ordered-list']}>
                <ListItemText className={styles['list-item']}>
                    <span>Click the button to start a new Inventory Check.</span>
                </ListItemText>
                <ListItemText className={styles['list-item']}>
                    <span>A prompt will appear with the basic information for the new Inventory Check, including:</span>
                    <ul>
                        <li>
                            <b>Name:</b> The name of the new Inventory Check, automatically populated based on the timestamp when it is created.
                        </li>
                        <li>
                            <b>Start Date:</b> The date and time when the new Inventory Check was created.
                        </li>
                        <li>
                            <b>Cancel:</b> Closes the prompt without creating an Inventory Check.
                        </li>
                        <li>
                            <b>Create:</b> Confirms the information and creates the new Inventory Check.
                        </li>
                    </ul>
                </ListItemText>
                <ListItemText className={styles['list-item']}>
                    <span>
                        Clicking <b>Create</b> will create an empty Inventory Check, and it will navigate to the Inventory Check detail page so you
                        can begin the session.
                    </span>
                </ListItemText>
            </List>
            <p>
                <i>* Only privileged users can add new Inventory Check.</i>
            </p>
        </Box>
    );

    const renderOpenInstruction = () => (
        <Box className={styles['instruction-child-container']}>
            <List>
                <ListItemText>
                    <span>
                        <b>Double-click</b> on an Inventory Check or click the three dot menu on the row of an Inventory Check and choose <b>View</b>{' '}
                        to open the Inventory Check details.
                    </span>
                </ListItemText>
            </List>
        </Box>
    );

    const renderRemoveInstruction = () => (
        <Box className={styles['instruction-child-container']}>
            <List className={styles['ordered-list']}>
                <ListItemText className={styles['list-item']}>
                    <span>
                        Click the three dot menu for an Inventory Check, then choose <b>Remove</b> to open the confirmation prompt.
                    </span>
                </ListItemText>
                <ListItemText className={styles['list-item']}>
                    <span>
                        Click <b>Yes</b> to confirm removal of the Inventory Check. This action cannot be undone.
                    </span>
                </ListItemText>
                <ListItemText className={styles['list-item']}>
                    <span>
                        Click <b>Cancel</b> to close the confirmation prompt and return to the Inventory Check List.
                    </span>
                </ListItemText>
            </List>
            <p>
                <i>* Only privileged users can remove Inventory Check.</i>
            </p>
        </Box>
    );

    const renderSearchInstruction = () => (
        <Box className={styles['instruction-child-container']}>
            <List className={styles['ordered-list']}>
                <ListItemText className={styles['list-item']}>
                    <span>Type keywords into the Search box.</span>
                </ListItemText>
                <ListItemText className={styles['list-item']}>
                    <span>Press Enter to start searching.</span>
                </ListItemText>
                <ListItemText className={styles['list-item']}>
                    <span>The Inventory Check List will narrow down to display only Inventory Check that match the entered keywords.</span>
                </ListItemText>
                <ListItemText className={styles['list-item']}>
                    <span>A message will appear if no matching Inventory Check are found in the viewing Inventory Check List.</span>
                </ListItemText>
            </List>
        </Box>
    );

    const renderDivider = () => <Divider className={styles['divider']} />;

    const renderOverviewSection = () => (
        <>
            <h4>OVERVIEW</h4>
            <p className={styles['description']}>
                The Inventory Check feature allows Lasta users to perform a checklist comparison with the <b>Current Inventory</b> of a selected
                Location. This enables users to:
            </p>
            <List className={styles['unordered-list']}>
                <ListItemText className={styles['list-item']}>
                    <span>
                        <b>Compare</b> the physical presence of vehicles or equipment at a Location with the digital records stored in Current
                        Inventory of Lasta.
                    </span>
                </ListItemText>
                <ListItemText className={styles['list-item']}>
                    <span>
                        Determine and take <b>corrective actions</b> for any newly found, mismatched, or updated items in the Current Inventory of
                        Lasta.
                    </span>
                </ListItemText>
            </List>
            <p className={styles['description']}>An Inventory Check can have one of the following statuses:</p>
            <List>
                <ListItemText>
                    <span>
                        <b>Started:</b> The Inventory Check has been newly created, and no actions have been taken on its checklist.
                    </span>
                </ListItemText>
                <ListItemText>
                    <span>
                        <b>In Progress:</b> The Inventory Check has been created, and users have performed some actions on its checklist.
                    </span>
                </ListItemText>
                <ListItemText>
                    <span>
                        <b>Review then Complete:</b> Users have completed the actions and are now reviewing all changes to decide on the necessary
                        remedial actions for each item in the Inventory Check then have finalized the remedial actions and pushed all changes to the
                        current inventory at the committed timestamp.
                    </span>
                </ListItemText>
            </List>
        </>
    );

    const renderInstructionsSection = () => (
        <>
            <h4>INSTRUCTIONS</h4>
            <Box className={styles['instructions-accordion-container']} sx={{ width: '100%' }}>
                {instructions.map((instruction, index) => (
                    <Accordion
                        key={instruction.key}
                        defaultExpanded={index === 0}
                        disableGutters
                        className={styles['instruction-accordion']}
                        sx={{ width: '100%' }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`${instruction.key}-content`}
                            id={`${instruction.key}-header`}
                            className={styles['instruction-accordion-summary']}
                            sx={{ width: '100%' }}
                        >
                            <Typography className={styles['instruction-header']}>{instruction.title}</Typography>
                        </AccordionSummary>
                        <AccordionDetails className={styles['instruction-accordion-details']} sx={{ width: '100%' }}>
                            {getInstructionComponent(instruction.key)}
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        </>
    );

    const renderFooter = () => (
        <Stack className={styles['footer']}>
            <span>
                {`For further support, email us at `}
                <Link href="mailto:cs@birdseye.ca" underline="none">
                    cs@birdseye.ca
                </Link>
                {'.'}
            </span>
        </Stack>
    );

    return (
        <Stack className={styles['help-dialog-container']}>
            <Box className={styles['help-dialog-content-container']}>
                {renderOverviewSection()}
                {renderDivider()}
                {renderInstructionsSection()}
            </Box>
            {renderFooter()}
        </Stack>
    );
};

export default HelpDialog;
